import { useState } from "react";
import AdminButton from "../../../core/admin/adminButton";
import AdminToast from "../../../core/admin/adminToast";
import AdminPage from "../../../core/admin/adminPage";
import AdminCard from "../../../core/admin/adminCard";
import AdminTable from "../../../core/admin/adminTable";
import AdminTextInput from "../../../core/admin/adminTextInput";
import AdminEmptyState from "../../../core/admin/adminEmptyState";
import { AdminSelect } from "../../../core/admin/adminField";
import { AdminSegmented } from "../../../core/admin/adminTabs";
import { AdminCount, WatchStatusBadge } from "../../../core/admin/adminBadge";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import { SearchLineIcon, WatchLineIcon } from "../../../../img/svg/adminIcons";
import { useWatches } from "../../../../context/watchProvider";
import WatchModal from "./watchModal";
import { statusFields } from "../../../../utils/fields";

export interface IWatch {
    id: string;
    [key: string]: any;
}

export default function AdminWatches() {

    const { getWatches, getWatch } = useWatches();

    const [watches, setWatches] = useState<IWatch[] | []>([]);
    const [loading, setLoading] = useState(false);
    const [badToast, setBadToast] = useState(false);
    const [status, setStatus] = useState("display");
    const [modal, setModal] = useState(false);
    const [editableWatch, setEditableWatch] = useState<IWatch | null>(null);
    const [searchParameter, setSearchParameter] = useState("status");
    const [serial, setSerial] = useState("");

    const doGetWatches = async () => {
        setLoading(true);
        let parameter = "";
        if (searchParameter === "id") {
            const watchResponse = await getWatch(serial);
            if (!watchResponse) {
                setLoading(false);
                setWatches([]);
                setBadToast(true);
                return null;
            }
            setWatches([watchResponse]);
            setLoading(false);
            return null;
        }
        if (searchParameter === "status") {
            parameter = status;
        }
        else if (searchParameter === "serial" || searchParameter === "searchTerms") {
            parameter = serial;
        }
        else {
            return null;
        }
        const watchesResponse = await getWatches(searchParameter, searchParameter === "serial" ? parameter?.toUpperCase() : parameter?.toLowerCase(), searchParameter === "searchTerms" ? "array-contains" : "==");
        if (!watchesResponse) {
            setLoading(false);
            setWatches([]);
            setBadToast(true);
            return null;
        }
        setWatches(watchesResponse);
        setLoading(false);
    }

    const handleWatchModal = (w: IWatch) => {
        setEditableWatch(w);
        setModal(true);
    }

    const hasSold = watches?.some(w => w.status === "sold");

    const inputPlaceholder: { [key: string]: string } = {
        searchTerms: "Ex.: rolex, submariner",
        serial: "Número de série",
        id: "ID do documento",
    }

    return (
        <AdminPage title="Relógios cadastrados" description="Busque relógios por status, marca/modelo, serial ou ID para ver os detalhes e alterar o status.">
            <AdminCard>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-semibold text-gray-700">Buscar por</span>
                        <AdminSegmented
                            value={searchParameter}
                            onChange={setSearchParameter}
                            options={[
                                { value: "status", label: "Status" },
                                { value: "searchTerms", label: "Marca/Modelo" },
                                { value: "serial", label: "Serial" },
                                { value: "id", label: "ID" },
                            ]}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
                        {
                            searchParameter === "status" ?
                                <AdminSelect value={status} onChange={e => setStatus(e.target.value)}>
                                    {
                                        Object.keys(statusFields).map((o, i) => (
                                            <option value={o} key={i}>{statusFields[o]}</option>
                                        ))
                                    }
                                </AdminSelect>
                                : ["serial", "id", "searchTerms"].includes(searchParameter) ?
                                    <AdminTextInput value={serial} onChange={e => setSerial(e.target.value)} onEnter={doGetWatches} placeholder={inputPlaceholder[searchParameter]} />
                                    : null
                        }
                        <AdminButton icon={<SearchLineIcon width="16px" />} loading={loading} onClick={doGetWatches}>Listar</AdminButton>
                    </div>
                </div>
            </AdminCard>

            <AdminCard title={<>Resultados {watches.length ? <AdminCount count={watches.length} /> : null}</>} bodyClassName="p-3 md:p-4">
                {
                    loading ?
                        <AdminLoading />
                        : watches.length ?
                            <AdminTable
                                rows={watches}
                                rowKey={(wtc, idx) => wtc?.id || idx}
                                columns={[
                                    { header: "Relógio", mobile: "title", desktopHidden: true, render: wtc => `${wtc?.brand || ""} ${wtc?.model || ""}` },
                                    { header: "Status", mobile: "aside", desktopHidden: true, render: wtc => <WatchStatusBadge status={wtc?.status} /> },
                                    { header: "ID", render: wtc => <span className="font-mono text-xs text-gray-500 break-all">{wtc?.id}</span> },
                                    { header: "Marca", mobile: "hidden", render: wtc => <span className="font-semibold">{wtc?.brand}</span> },
                                    { header: "Modelo", mobile: "hidden", render: wtc => wtc?.model },
                                    { header: "Ano", render: wtc => wtc?.year },
                                    { header: "Status", mobile: "hidden", render: wtc => <WatchStatusBadge status={wtc?.status} /> },
                                    ...(hasSold ? [{ header: "Vendido em", className: "whitespace-nowrap", render: (wtc: IWatch) => wtc?.soldAt?.toDate().toLocaleString() || "—" }] : []),
                                ]}
                                actions={wtc => <AdminButton size="sm" variant="secondary" onClick={() => handleWatchModal(wtc)}>Ver</AdminButton>}
                            />
                            :
                            <AdminEmptyState icon={<WatchLineIcon />} title="Nenhum relógio listado" description="Escolha um filtro acima e clique em “Listar”." />
                }
            </AdminCard>
            <AdminToast message="Nenhum resultado encontrado" toast={badToast} setToast={setBadToast} tone="danger" />
            {
                modal ?
                    <WatchModal close={() => setModal(false)} editableWatch={editableWatch} refresh={doGetWatches} />
                    : null
            }
        </AdminPage>
    )
}
